import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const CardListContainer = styled.div`
  padding: 12px;
  background: #f5f5f5;
  border-radius: 3px;
  margin-top: 15px;

  @media all and (min-width: 768px) {
    padding: 20px 24px;
    margin-top: 33px;
  }
`;

const CardListTitle = styled.span`
  display: block;
  font-family: 'Open Sans';
  font-size: 16px;
  line-height: 30px;
  color: #666666;
  margin-bottom: 20px;

  @media all and (min-width: 768px) {
    font-size: 24px;
  }
`;

const CardListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  @media all and (min-width: 450px) {
    font-size: 24px;
    justify-content: flex-start;
  }
`;

const Card = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: normal;
  align-items: center;
  height: 170px;
  width: 130px;
  margin-bottom: 12px;
  margin-right: 9px;
  background: #ffffff;
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.05);
  border-radius: 3px;

  @media all and (min-width: 450px) {
    margin-right: 12px;
  }

  @media all and (min-width: 768px) {
    height: 200px;
    width: 170px;
  }
`;

const CardImageContainer = styled.div`
  width: 90%;
  height: 50px;
  justify-self: flex-start;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-bottom: 14px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: 50% 50%;

  @media all and (min-width: 768px) {
    margin-top: 20px;
    height: 65px;
  }

  &.rounded {
    width: 50px;
    border-radius: 50%;

    @media all and (min-width: 768px) {
      width: 65px;
      height: 65px;
    }
  }
`;

const CardTextContainer = styled.div`
  display: block;
  flex-grow: 1;
`;

const CardTitle = styled.span`
  display: block;
  font-family: 'Open Sans';
  font-size: 12px;
  font-weight: bold;
  line-height: 20px;
  text-transform: capitalize;
  color: #666666;
  text-align: center;
  padding-left: 15px;
  padding-right: 15px;

  @media all and (min-width: 768px) {
    font-size: 14px;
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const CardSubtitle = styled.span`
  display: block;
  font-family: 'Open Sans';
  font-size: 10px;
  line-height: 18px;
  text-transform: capitalize;
  color: #999999;
  text-align: center;

  @media all and (min-width: 768px) {
    font-size: 12px;
  }
`;

const CardLink = styled(Link)`
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  text-decoration-line: underline;
  text-transform: capitalize;
  color: #999999;
  justify-self: flex-end;
  padding-bottom: 10px;

  @media all and (min-width: 768px) {
    padding-bottom: 20px;
  }
`;

const CardAnchor = styled.a`
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 18px;
  text-decoration-line: underline;
  text-transform: capitalize;
  color: #999999;
  justify-self: flex-end;
  padding-bottom: 10px;

  @media all and (min-width: 768px) {
    padding-bottom: 20px;
  }
`;

const CardAdd = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 170px;
  width: 130px;
  margin-bottom: 12px;
  margin-right: 9px;
  background: #f26e26;
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.05);
  border-radius: 3px;
  cursor: pointer;

  @media all and (min-width: 450px) {
    margin-right: 12px;
  }

  @media all and (min-width: 768px) {
    height: 200px;
    width: 170px;
  }

  .plus-icon {
    width: 45px;
    height: 45px;
    background-image: url(/images/market_map/plus-white.svg);
    background-size: contain;
    background-repeat: no-repeat;
    background-position: 50% 50%;
    margin-bottom: 24px;
  }

  .add-text {
    font-family: 'Open Sans';
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    text-transform: uppercase;
    color: #ffffff;
  }
`;

function CardList({
  title,
  list,
  add = false,
  addText = 'Add',
  addHandler = null,
}) {
  const handleOnAdd = () => {
    if (addHandler) {
      addHandler();
    }
  };
  return (
    <CardListContainer>
      {title && <CardListTitle>{title}</CardListTitle>}
      <CardListWrapper>
        {list &&
          list.length > 0 &&
          list.map((card, index) => (
            <Card key={card.id ?? index}>
              <CardImageContainer
                style={{
                  ...(card.imageSrc && {
                    backgroundImage: `url(${card.imageSrc})`,
                  }),
                }}
                className={`${card.imgRounded ? 'rounded' : ''}`}
              />
              <CardTextContainer>
                {card.title && <CardTitle>{card.title}</CardTitle>}
                {card.subtitle && <CardSubtitle>{card.subtitle}</CardSubtitle>}
              </CardTextContainer>
              {card.link && (
                <>
                  {card.linkTo && card.linkTo === 'remote' && (
                    <CardAnchor
                      href={card.link}
                      {...(card.linkNewTab && {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                      })}
                    >
                      {card.linkText ?? 'Link'}
                    </CardAnchor>
                  )}
                  {(!card.linkTo ||
                    (card.linkTo && card.linkTo !== 'remote')) && (
                    <CardLink
                      to={card.link}
                      {...(card.linkNewTab && {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                      })}
                    >
                      {card.linkText ?? 'Link'}
                    </CardLink>
                  )}
                </>
              )}
            </Card>
          ))}
        {add && (
          <CardAdd onClick={() => handleOnAdd()}>
            <div className="plus-icon"></div>
            <div className="add-text">{addText}</div>
          </CardAdd>
        )}
      </CardListWrapper>
    </CardListContainer>
  );
}

export default CardList;
